@if (currentGraph(); as graph) {
  @if (currentAssetId(); as assetId) {
    <app-graph-header
      [breadcrumbTrail]="breadcrumbTrail()"
      [liveRegionMessage]="liveRegionMessage()"
      [graphName]="graphName()"
      [graphTypeLabel]="graphTypeLabel()"
      [graphDescription]="graphDescription()"
      [menuItems]="menuItems()"
      [showDateNavigation]="graph.type === 1 || graph.type === 3"
      [showRefreshControls]="true"
      [dateNav]="dateNav"
      [canRefresh]="canRefreshConditions()"
      [autoRefresh]="autoRefresh()"
      [canSave]="isDirty()"
      [assetId]="assetId"
      (save)="onSave()"
      (refresh)="onRefresh()"
      (autoRefreshChange)="onAutoRefreshChange($event)" />
  }

  <div class="px-(--space-4)">
    <div
      cesFullscreen
      #graphFs="cesFullscreen"
      class="flex flex-col md:grid mb-(--space-6) rounded-[var(--radius-md)] p-(--space-6)"
      [class.bg-white]="graphFs.isFullscreen()"
      [class.bg-[#F6F6F8]]="!graphFs.isFullscreen()"
      [style.height]="graphFs.isFullscreen() ? '100vh' : null"
      [style.grid-template-rows]="graphFs.isFullscreen() ? '1fr' : null"
      [class.md:grid-cols-[1fr_16px_240px]]="!legendMgr.panelCollapsed()"
      [class.md:grid-cols-[1fr_16px]]="legendMgr.panelCollapsed()">
      <div class="min-w-0">
        <app-graph-chart
          [data]="plotlyDataForChart()"
          [layout]="plotlyChartLayout()"
          [config]="plotlyConfig"
          [revision]="plotlyRevision()"
          [filename]="graphName()"
          [isLoading]="isLoadingMeasures()"
          [ariaLabel]="'Graphique ' + graphTypeLabel() + ': ' + graphName()"
          [height]="graphFs.isFullscreen() ? 'h-full' : graphHeight"
          (pointClick)="onPointClick($event)"
          (xRangeChange)="onXRangeChange($event)"
          (resetAxes)="onResetAxes()"
          (autoscale)="onAutoscale()"
          (autocenter)="onAutocenter()"
          (fullscreenToggle)="graphFs.toggle()" />
      </div>

      <button
        type="button"
        (click)="onToggleLegendPanel()"
        class="hidden md:flex items-center justify-center self-stretch bg-transparent hover:bg-white/40 rounded-full transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--p-primary-color)] group"
        [attr.aria-label]="legendMgr.panelCollapsed() ? ('graphs.legend.show' | transloco) : ('graphs.legend.hide' | transloco)"
        [attr.aria-expanded]="!legendMgr.panelCollapsed()">
        <i
          class="pi text-[10px] text-[#AAA] group-hover:text-[#555] transition-colors"
          [class.pi-chevron-right]="!legendMgr.panelCollapsed()"
          [class.pi-chevron-left]="legendMgr.panelCollapsed()"></i>
      </button>

      @if (!legendMgr.panelCollapsed()) {
        <div class="flex flex-col {{ graphHeight }}">
          <h3 class="font-medium mb-(--space-3) flex-shrink-0">{{ 'graphs.legend.title' | transloco }}</h3>
          <app-chart-legend
            class="flex-1 min-h-0"
            [plotlyData]="legendItems()"
            [dragFeedback]="legendMgr.dragFeedback()"
            [disabled]="!legendInteractive()"
            (toggleVisibility)="onToggleTraceVisibility($event)"
            (configure)="onConfigureTrace($event)"
            (drop)="onDrop($event)"
            (keyDown)="onLegendKeyDown($event.event, $event.index)" />
        </div>
      }
      <!-- Dans le conteneur fullscreen : hors de lui, le dialogue serait rendu sous la top layer
           du plein écran natif et invisible (retour UAT 106). position:fixed reste ancré au viewport. -->
      @if (selectedPoint(); as point) {
        <div
          #pointInfoPanel
          role="dialog"
          aria-modal="true"
          aria-live="assertive"
          aria-labelledby="point-info-title"
          tabindex="-1"
          (keydown.escape)="closePointInfo()"
          cdkTrapFocus
          [cdkTrapFocusAutoCapture]="true"
          class="fixed z-[1000] bg-white rounded-[var(--radius-md)] shadow-lg border border-[var(--p-surface-200)] p-(--space-4) w-[320px]"
          [style.left.px]="pointInfoPosition().x"
          [style.top.px]="pointInfoPosition().y">
          <div class="flex flex-col gap-(--space-3)">
            <div class="flex justify-between items-start">
              <h4 id="point-info-title" class="font-semibold text-base m-0">{{ 'graphs.pointInfo.title' | transloco }}</h4>
              <button
                type="button"
                (click)="closePointInfo()"
                (keydown.enter)="closePointInfo()"
                (keydown.space)="closePointInfo()"
                [attr.aria-label]="'common.accessibility.close' | transloco"
                class="text-[var(--p-surface-500)] hover:text-[var(--p-surface-700)] cursor-pointer bg-transparent border-none p-0">
                <i class="pi pi-times text-sm" aria-hidden="true"></i>
              </button>
            </div>
            <div class="flex flex-col gap-(--space-2) text-sm">
              <div class="flex justify-between">
                <span class="text-(color:--text-secondary)">{{ 'graphs.labels.curve' | transloco }} :</span>
                <span class="font-medium text-right max-w-[180px] truncate" [title]="point.traceName">{{ point.traceName }}</span>
              </div>
              @if (getMeasureName(point.measureId); as measureName) {
                <div class="flex justify-between">
                  <span class="text-(color:--text-secondary)">{{ 'graphs.labels.measure' | transloco }} :</span>
                  <span class="font-medium text-right max-w-[180px] truncate" [title]="measureName">{{ measureName }}</span>
                </div>
              }
              <div class="flex justify-between">
                <span class="text-(color:--text-secondary)">{{ 'graphs.labels.xValue' | transloco }} :</span>
                <span class="font-medium">{{ formatPointValue(point.xValue) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-(color:--text-secondary)">{{ 'graphs.labels.yValue' | transloco }} :</span>
                <span class="font-medium">{{ formatPointValue(point.yValue) }}</span>
              </div>
              @if (point.measureType === MeasureType.MANUAL && point.campaignId) {
                <div class="flex justify-between">
                  <span class="text-(color:--text-secondary)">{{ 'graphs.labels.link' | transloco }} :</span>
                  <a
                    [href]="campaignUrl()"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-medium text-right max-w-[180px] truncate underline"
                    [title]="campaignName() || ('graphs.pointInfo.campaignFallback' | transloco: { id: point.campaignId })">
                    {{ campaignName() || ('graphs.pointInfo.campaignFallback' | transloco: { id: point.campaignId }) }}
                  </a>
                </div>
              }
              @if (measureFollowUpUrl(); as followUpUrl) {
                <div class="flex justify-between">
                  <span class="text-(color:--text-secondary)">{{ 'graphs.labels.link' | transloco }} :</span>
                  <a
                    [href]="followUpUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-medium text-right max-w-[180px] truncate underline"
                    [title]="getMeasureName(point.measureId)">
                    {{ getMeasureName(point.measureId) }}
                  </a>
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>

    @switch (graph.type) {
      @case (1) {
        <app-measure-selector-ev-temp
          [selectedMeasures]="measureMgr.selectedMeasuresEvTemp()"
          (selectMeasures)="selectMeasures()"
          (configureMeasure)="configureMeasureEvTemp($event)"
          (removeMeasure)="removeMeasureEvTemp($event)" />
      }
      @case (2) {
        <app-measure-selector-multi-year
          [selectedMeasure]="measureMgr.selectedMeasureMultiYear()"
          [yearType]="yearType()"
          [availableYears]="availableYears()"
          [isYearTypeLocked]="isYearTypeLocked()"
          (addMeasure)="addAxisMeasure('multiYear')"
          (configureMeasure)="configureMeasure('multiYear')"
          (removeMeasure)="removeMeasure('multiYear')"
          (yearTypeChange)="onYearTypeChange($event)"
          (addYear)="onAddYear($event)"
          (removeYear)="onRemoveYear($event)" />
      }
      @case (3) {
        <div class="flex flex-col gap-4">
          <app-measure-selector-xy
            [selectedMeasureX]="measureMgr.selectedMeasureX()"
            [selectedMeasureY]="measureMgr.selectedMeasureY()"
            (addMeasureX)="addAxisMeasure('x')"
            (configureMeasureX)="configureMeasure('x')"
            (removeMeasureX)="removeMeasure('x')"
            (addMeasureY)="addAxisMeasure('y')"
            (configureMeasureY)="configureMeasure('y')"
            (removeMeasureY)="removeMeasure('y')" />
          <app-envelope-manager
            [envelopes]="envelopeMgr.envelopes()"
            (addEnvelope)="addEnvelope()"
            (configureEnvelope)="configureEnvelope($event)"
            (removeEnvelope)="removeEnvelope($event)" />
        </div>
      }
    }
  </div>
}
